import {
  ConnectionState,
  Conversation,
  ConversationUpdateReason,
  Message,
  MessageUpdateReason,
  Participant,
  ParticipantUpdateReason,
} from '@twilio/conversations';
import type {SendFn} from '../setupMessages';
import type {ClientEvents, ConnectionError} from './types';
import {TwilioApiService} from './TwilioApiService';
import {Serializer} from './Serializer';

interface IClientEvents extends Partial<ClientEvents> {
  register: (service: TwilioApiService) => void;
  clear: (service: TwilioApiService) => void;
}

/**
 * Handler for events sent from STP Twilio to the client. It implements the IClientEvents
 * interface and can optionally implement the event methods. This ensures the event signature
 * is correctly defined and works with Twilio Conversation.
 *
 * @method {register} registers the event listeners defined by the handler
 * @method {clear} clears all the event listeners
 */
export class ClientEventsHandler implements IClientEvents {
  send: SendFn<'TwilioConversations'>;

  constructor(respond: SendFn<'TwilioConversations'>) {
    this.send = respond;
  }

  initialized = () => {
    this.send({
      event: 'initialized',
    });
  };

  initFailed = ({error}: {error?: ConnectionError}) => {
    this.send({
      event: 'initFailed',
      data: error,
    });
  };

  conversationAdded = (conversation: Conversation) => {
    this.send({
      event: 'conversationAdded',
      data: Serializer.fromConversation(conversation),
    });
  };

  conversationJoined = (conversation: Conversation) => {
    this.send({
      event: 'conversationJoined',
      data: Serializer.fromConversation(conversation),
    });
  };

  conversationLeft = (conversation: Conversation) => {
    this.send({
      event: 'conversationLeft',
      data: Serializer.fromConversation(conversation),
    });
  };

  conversationRemoved = (conversation: Conversation) => {
    this.send({
      event: 'conversationRemoved',
      data: Serializer.fromConversation(conversation),
    });
  };

  conversationUpdated = (data: {
    conversation: Conversation;
    updateReasons: ConversationUpdateReason[];
  }) => {
    this.send({
      event: 'conversationUpdated',
      data: {
        conversation: Serializer.fromConversation(data.conversation),
        updateReasons: data.updateReasons,
      },
    });
  };

  participantJoined = async (participant: Participant) => {
    this.send({
      event: 'participantJoined',
      data: await Serializer.fromParticipant(participant),
    });
  };

  participantLeft = async (participant: Participant) => {
    this.send({
      event: 'participantLeft',
      data: await Serializer.fromParticipant(participant),
    });
  };

  participantUpdated = async (data: {
    participant: Participant;
    updateReasons: ParticipantUpdateReason[];
  }) => {
    this.send({
      event: 'participantUpdated',
      data: {
        participant: await Serializer.fromParticipant(data.participant),
        updateReasons: data.updateReasons,
      },
    });
  };

  typingStarted = async (participant: Participant) => {
    this.send({
      event: 'typingStarted',
      data: await Serializer.fromParticipant(participant),
    });
  };

  typingEnded = async (participant: Participant) => {
    this.send({
      event: 'typingEnded',
      data: await Serializer.fromParticipant(participant),
    });
  };

  messageAdded = async (message: Message) => {
    this.send({
      event: 'messageAdded',
      data: await Serializer.fromMessage(message),
    });
  };

  messageRemoved = async (message: Message) => {
    this.send({
      event: 'messageRemoved',
      data: await Serializer.fromMessage(message),
    });
  };

  messageUpdated = async (data: {
    message: Message;
    updateReasons: MessageUpdateReason[];
  }) => {
    this.send({
      event: 'messageUpdated',
      data: {
        message: await Serializer.fromMessage(data.message),
        updateReasons: data.updateReasons,
      },
    });
  };

  connectionError = (data: ConnectionError) => {
    this.send({
      event: 'connectionError',
      data,
    });
  };

  connectionStateChanged = (state: ConnectionState) => {
    this.send({
      event: 'connectionStateChanged',
      data: state,
    });
  };

  register(service: TwilioApiService) {
    service.registerClientEventCallback('initialized', this.initialized);
    service.registerClientEventCallback('initFailed', this.initFailed);
    service.registerClientEventCallback(
      'conversationAdded',
      this.conversationAdded,
    );
    service.registerClientEventCallback(
      'conversationJoined',
      this.conversationJoined,
    );
    service.registerClientEventCallback(
      'conversationLeft',
      this.conversationLeft,
    );
    service.registerClientEventCallback(
      'conversationRemoved',
      this.conversationRemoved,
    );
    service.registerClientEventCallback(
      'conversationUpdated',
      this.conversationUpdated,
    );
    service.registerClientEventCallback(
      'participantJoined',
      this.participantJoined,
    );
    service.registerClientEventCallback(
      'participantLeft',
      this.participantLeft,
    );
    service.registerClientEventCallback(
      'participantUpdated',
      this.participantUpdated,
    );
    service.registerClientEventCallback('messageAdded', this.messageAdded);
    service.registerClientEventCallback('messageRemoved', this.messageRemoved);
    service.registerClientEventCallback('messageUpdated', this.messageUpdated);
    service.registerClientEventCallback('typingStarted', this.typingStarted);
    service.registerClientEventCallback('typingEnded', this.typingEnded);
    service.registerClientEventCallback(
      'connectionError',
      this.connectionError,
    );
    service.registerClientEventCallback(
      'connectionStateChanged',
      this.connectionStateChanged,
    );
  }

  // eslint-disable-next-line class-methods-use-this
  clear(service: TwilioApiService) {
    service.clearClientEventCallback();
  }
}
